import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BackMethods from "../../back_component/BackMethods";
import {
    FaClipboardList, FaEye, FaPhoneAlt, FaSearch,
    FaCalendarDay, FaExclamationCircle, FaStore, FaCheckCircle, FaClock
} from "react-icons/fa";

export default function ComplaintsDashboard() {
    const location = useLocation();
    const { backEnd_request } = BackMethods();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [allComplaints, setAllComplaints] = useState([]);
    const [filteredComplaints, setFilteredComplaints] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeTab, setActiveTab] = useState("unprocessed");

    const truncateWords = (text, limit) => {
        if (!text) return "---";
        const words = text.split(" ");
        if (words.length > limit) return words.slice(0, limit).join(" ") + "...";
        return text;
    };

    const fetchComplaints = async (statusType) => {
        setLoading(true);
        try {
            const endpoint = statusType === "processed"
                ? "/public/staff/complaints/processed"
                : "/public/staff/complaints/unprocessed";

            const response = await backEnd_request.get(endpoint);
            setAllComplaints(response.data || []);
        } catch (err) {
            console.error("Error:", err);
            setAllComplaints([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchComplaints(activeTab);
    }, [activeTab]);

    useEffect(() => {
        const results = allComplaints.filter(c =>
            (c.submitterPhone?.includes(searchTerm)) ||
            (c.complaintText?.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (c.establishment?.name?.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        setFilteredComplaints(results);
    }, [searchTerm, allComplaints]);

    return (
        <div className="complaints-wrapper bg-light min-vh-100 py-4" dir="rtl">
            <div className="container-fluid px-4">

                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4 bg-white p-3 rounded-4 shadow-sm border-bottom border-primary border-3">
                    <div className="d-flex align-items-center gap-3">
                        <div className="bg-primary text-white p-2 rounded-3">
                            <FaClipboardList size={22} />
                        </div>
                        <h4 className="fw-bold text-dark m-0 fs-5">سجل الشكاوي</h4>
                    </div>
                    <button onClick={() => navigate(-1)} className="btn btn-dark rounded-pill px-4 btn-sm">رجوع</button>
                </div>

                {/* Tabs */}
                <div className="row mb-4">
                    <div className="col-md-5 mx-auto">
                        <div className="tab-container bg-white p-1 rounded-pill shadow-sm d-flex border">
                            <button
                                className={`tab-btn ${activeTab === 'unprocessed' ? 'active unprocessed' : ''}`}
                                onClick={() => setActiveTab("unprocessed")}
                            >
                                <FaClock className="ms-2" /> قيد الانتظار
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'processed' ? 'active processed' : ''}`}
                                onClick={() => setActiveTab("processed")}
                            >
                                <FaCheckCircle className="ms-2" /> معالجة
                            </button>
                        </div>
                    </div>
                </div>

                {/* Search */}
                <div className="search-box mb-4">
                    <div className="input-group shadow-sm rounded-4 border-0 bg-white">
                        <span className="input-group-text bg-transparent border-0 pe-4"><FaSearch className="text-muted" /></span>
                        <input
                            type="text"
                            className="form-control border-0 py-3 bg-transparent"
                            placeholder="ابحث برقم الهاتف أو اسم المنشأة..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary"></div>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table custom-modern-table">
                            <thead>
                                <tr>
                                    <th className="text-center">#</th>
                                    <th>المنشأة</th>
                                    <th>مضمون الشكوى</th>
                                    <th>المشتكي</th>
                                    <th>التاريخ</th>
                                    <th className="text-center">الإجراء</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredComplaints.length > 0 ? filteredComplaints.map((c, index) => (
                                    <tr key={c.complaintUid}>
                                        <td className="text-center fw-bold text-muted">{index + 1}</td>
                                        <td>
                                            <div className="fw-bold text-dark"><FaStore className="ms-1 text-primary" size={14} /> {c.establishment?.name}</div>
                                            <div className="text-muted small-text">UID: {c.establishment?.fieldUID?.slice(0, 8)}</div>
                                        </td>
                                        <td className="complaint-text-cell">
                                            <p className="m-0 text-dark lh-base">{truncateWords(c.complaintText, 12)}</p>
                                        </td>
                                        <td>
                                            <div className="fw-bold text-primary mb-1"><FaPhoneAlt size={11} className="ms-1" /> {c.submitterPhone}</div>
                                            <span className={`status-badge ${activeTab === 'processed' ? 'success' : 'warning'}`}>
                                                {activeTab === 'processed' ? 'مكتملة' : 'بانتظار الإجراء'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="text-dark fw-medium small"><FaCalendarDay className="ms-1 text-info" size={12} /> {c.createdAtUtc?.split("T")[0]}</div>
                                            <div className="text-muted small-text">{c.createdAtUtc?.split("T")[1].slice(0, 5)}</div>
                                        </td>
                                        <td className="text-center">
                                            <button className="btn-view" onClick={() => navigate(`/admin/complaint-details?uid=${c.complaintUid}`)}>
                                                <FaEye />
                                            </button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5 no-data">
                                            <FaExclamationCircle className="fs-2 mb-2 d-block mx-auto opacity-25" />
                                            لا توجد شكاوى حالياً
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <style>{`
                .complaints-wrapper { font-family: 'Cairo', sans-serif; }
                
                /* Tab Styling */
                .tab-container { width: 100%; }
                .tab-btn { 
                    flex: 1; border: none; background: transparent; padding: 10px; 
                    border-radius: 50px; color: #6c757d; font-weight: 600; transition: 0.3s;
                    display: flex; align-items: center; justify-content: center;
                }
                .tab-btn.active.unprocessed { background: #dc3545; color: white; box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3); }
                .tab-btn.active.processed { background: #198754; color: white; box-shadow: 0 4px 12px rgba(25, 135, 84, 0.3); }

                /* Table Styling - الحل الجذري للخلل */
                .custom-modern-table { border-collapse: separate !important; border-spacing: 0 12px !important; background: transparent; }
                .custom-modern-table thead th { border: none; padding: 10px 20px; color: #7a7a7a; font-weight: 600; font-size: 0.9rem; }
                
                .custom-modern-table tbody tr { 
                    background-color: white !important; 
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04); 
                    transition: 0.2s;
                }
                .custom-modern-table tbody tr:hover { transform: translateY(-3px); box-shadow: 0 5px 15px rgba(0,0,0,0.08); }
                
                .custom-modern-table td { 
                    padding: 18px 20px; 
                    border-top: 1px solid #f0f0f0 !important; 
                    border-bottom: 1px solid #f0f0f0 !important;
                    vertical-align: middle;
                }
                
                /* تعريب الزوايا للحواف */
                .custom-modern-table td:first-child { border-right: 1px solid #f0f0f0 !important; border-top-right-radius: 15px; border-bottom-right-radius: 15px; border-left: none; }
                .custom-modern-table td:last-child { border-left: 1px solid #f0f0f0 !important; border-top-left-radius: 15px; border-bottom-left-radius: 15px; border-right: none; }
                
                .small-text { font-size: 0.75rem; }
                .complaint-text-cell { max-width: 300px; font-size: 0.88rem; }

                /* Status Badges */
                .status-badge { 
                    font-size: 0.7rem; padding: 4px 10px; border-radius: 6px; font-weight: 600; 
                    display: inline-block; border: 1px solid;
                }
                .status-badge.warning { background: #fff3cd; color: #856404; border-color: #ffeeba; }
                .status-badge.success { background: #d1e7dd; color: #0f5132; border-color: #badbcc; }

                /* View Button */
                .btn-view {
                    width: 40px; height: 40px; border-radius: 12px; border: none;
                    background: #212529; color: white; transition: 0.3s;
                    display: flex; align-items: center; justify-content: center; margin: 0 auto;
                }
                .btn-view:hover { background: #0d6efd; transform: rotate(15deg); }
                
                .no-data { border-radius: 15px !important; border: 1px dashed #ccc !important; background: #fafafa !important; }
            `}</style>
        </div>
    );
}